import styled from '@emotion/styled'
import { tokens } from '@news-mono/design-tokens'
import { breakpoint, calcRem, fonts, metrics } from '../../__styling'

// TheNightly Colors
const tnColors = tokens.thenightly.colors
const tnPalette = tnColors.palette
// TheNightly Metrics
const tnMetrics = metrics.thenightly
const tnMargins = tnMetrics.margins

// PerthNow Colors
const pnColors = tokens.perthnow.colors
const pnPalette = pnColors.palette
// PerthNow Metrics
const pnMetrics = metrics.perthnow
const pnMargins = pnMetrics.margins

interface StyledUserSettingsInputProps {
    isFilled?: boolean
    hasError?: boolean
    marginBottom?: number
    hasIcon?: boolean
}

export const StyledUserSettingsInput = styled(
    'div',
)<StyledUserSettingsInputProps>(
    ({ isFilled, hasError, marginBottom, hasIcon, theme }) => [
        {
            fontFamily: theme.fonts.sansSerif,
            marginBottom: calcRem(marginBottom ?? tnMargins.lg),
            position: 'relative',

            // Styling for the actual input
            input: {
                padding: isFilled
                    ? calcRem(tnMargins.m2l, tnMargins.md, tnMargins.sm)
                    : calcRem(tnMargins.sm, tnMargins.md),
                width: hasIcon ? '90%' : '100%',
                height: '100%',
                fontSize: calcRem(16),
                fontWeight: 400,
                background: 'transparent',
                color: hasError
                    ? tnPalette.utility.error.default
                    : tnPalette.neutral[100],
                borderRadius: '8px',
                border: 'transparent',
                outline: 'none',

                '&:focus': {
                    padding: calcRem(tnMargins.m2l, tnMargins.md, tnMargins.sm),
                    border: 'transparent',
                    outline: 'none',

                    '&+label': {
                        fontSize: calcRem(tnMargins.sm2l),
                        top: calcRem(tnMargins.sm),
                        transform: 'translateY(0)',
                        color: hasError
                            ? tnPalette.utility.error.default
                            : isFilled
                            ? tnPalette.neutral[100]
                            : tnPalette.neutral[70],
                    },
                },

                [breakpoint('xxs')]: {
                    width: hasIcon ? '92%' : '100%',
                },
                [breakpoint('sm')]: {
                    width: hasIcon ? '93%' : '100%',
                },
                [breakpoint('md')]: {
                    width: hasIcon ? '94%' : '100%',
                },
                [breakpoint('lg')]: {
                    width: hasIcon ? '95%' : '100%',
                },
            },

            // Styling for the label for the input
            label: {
                position: 'absolute',
                fontSize: isFilled
                    ? calcRem(tnMargins.sm2l)
                    : calcRem(tnMargins.md),
                left: calcRem(tnMargins.md),
                top: isFilled ? calcRem(tnMargins.sm) : '50%',
                transform: isFilled ? 'translateY(0)' : 'translateY(-50%)',
                transition: 'all 0.2s',
                color: hasError
                    ? tnPalette.utility.error.default
                    : isFilled
                    ? tnPalette.neutral[100]
                    : tnPalette.neutral[70],
            },

            // Styling for the end icon
            svg: {
                position: 'absolute',
                right: calcRem(tnMargins.md),
                top: '50%',
                transform: 'translateY(-50%)',
                cursor: 'pointer',
            },
        },

        // PerthNow Styling
        theme.kind === 'perthnow' && {
            marginBottom: calcRem(marginBottom ?? 16),

            // Styling for the actual input
            input: {
                padding: calcRem(12, 12, 0),
                color: pnPalette.blackShade[85],

                '&:focus': {
                    padding: calcRem(12, 12, 0),

                    '&+label': {
                        fontSize: calcRem(12),
                        top: calcRem(8),
                        transform: 'translateY(0)',
                        color: isFilled
                            ? pnPalette.greyWeak
                            : pnPalette.blackShade[85],
                    },
                },
            },

            // Styled for the label for the input
            label: {
                top: isFilled ? calcRem(8) : '50%',
                left: calcRem(12),

                color: isFilled ? pnPalette.greyWeak : pnPalette.blackShade[85],

                // Text styling
                fontSize: isFilled ? calcRem(12) : calcRem(16),
                lineHeight: calcRem(16),
                fontWeight: 400,
            },
        },
    ],
)

export const StyledInnerUserSettingsInput = styled(
    'div',
)<StyledUserSettingsInputProps>(({ isFilled, hasError, theme }) => [
    {
        width: '100%',
        border: `1px solid ${
            hasError
                ? tnPalette.utility.error.default
                : isFilled
                ? tnPalette.neutral[70]
                : tnPalette.neutral[20]
        }`,
        borderRadius: calcRem(8),
        height: calcRem(56),
        fontSize: calcRem(16),
        fontWeight: 400,
        background: isFilled ? tnPalette.white : tnPalette.neutral[10],
        color: hasError
            ? tnPalette.utility.error.default
            : tnPalette.neutral[100],

        '&:focus-within': {
            background: tnPalette.white,
            outline: '5px auto -webkit-focus-ring-color',
        },
    },
    // PerthNow Styling
    theme.kind === 'perthnow' && {
        border: `1px solid ${pnPalette.blackShade[14]}`,
        borderRadius: calcRem(4),
        background: isFilled
            ? pnPalette.blackShade[1]
            : pnPalette.blackShade[4],

        height: calcRem(50),
    },
])
